import type { NotificationType as NotificationTypeType } from '@lombok/shared';

/**
 * Spanish copy for the Notificaciones slice. Each NotificationType maps to a
 * short label (used as a fallback title / chip) and an icon hint so the bell and
 * the full page render a recognizable category. Stored values stay snake_case;
 * the notification's own `title`/`body` (already Spanish from the backend) are
 * shown when present — these labels are the type-level fallback.
 */

export type NotificationTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

interface NotificationMeta {
  label: string;
  tone: NotificationTone;
}

export const NOTIFICATION_TYPE_META_ES: Record<NotificationTypeType, NotificationMeta> = {
  order_created: { label: 'Pedido creado', tone: 'info' },
  order_approved: { label: 'Pedido aprobado', tone: 'success' },
  order_rejected: { label: 'Pedido rechazado', tone: 'danger' },
  payment_uploaded: { label: 'Comprobante recibido', tone: 'info' },
  payment_approved: { label: 'Pago aprobado', tone: 'success' },
  payment_rejected: { label: 'Pago rechazado', tone: 'danger' },
  order_dispatched: { label: 'Pedido despachado', tone: 'info' },
  order_delivered: { label: 'Pedido entregado', tone: 'success' },
  invoice_due: { label: 'Factura por vencer', tone: 'warning' },
  invoice_overdue: { label: 'Factura vencida', tone: 'danger' },
  promo_available: { label: 'Promoción disponible', tone: 'info' },
  rebate_earned: { label: 'Rebate ganado', tone: 'success' },
  stock_available: { label: 'Disponibilidad de stock', tone: 'info' },
  account_approved: { label: 'Cuenta aprobada', tone: 'success' },
  credit_hold: { label: 'Retención de crédito', tone: 'warning' },
};

/** Resolve a notification type to its Spanish label (raw code as fallback). */
export function notificationTypeLabel(type: string): string {
  return NOTIFICATION_TYPE_META_ES[type as NotificationTypeType]?.label ?? type;
}

/** Resolve a notification type to its display tone (neutral fallback). */
export function notificationTone(type: string): NotificationTone {
  return NOTIFICATION_TYPE_META_ES[type as NotificationTypeType]?.tone ?? 'neutral';
}

/**
 * Relative-time label in Spanish for a notification timestamp (e.g. "hace 5 min").
 * Falls back to a short absolute date for anything older than a week or
 * unparseable input. Pure + dependency-free so it's cheap to call per row.
 */
export function relativeTimeEs(iso: string, now: number = Date.now()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const diffMs = Math.max(0, now - then);
  const min = Math.floor(diffMs / 60_000);
  if (min < 1) return 'hace un momento';
  if (min < 60) return `hace ${min} min`;
  const hours = Math.floor(min / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `hace ${days} d`;
  return new Date(then).toLocaleDateString('es-VE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}
